import { createContext, useContext, useState, useCallback, useEffect } from "react";

const LANG_KEY = "amd-lang";
export const SUPPORTED = ["en", "es", "zh", "vi"];
export const LANG_LABELS = { en: "English", es: "Español", zh: "中文", vi: "Tiếng Việt" };

// Language passed via ?hl= (used by SEO hreflang landing URLs), if supported.
export function urlLang() {
  try {
    const p = new URLSearchParams(window.location.search).get("hl");
    return SUPPORTED.includes(p) ? p : null;
  } catch {
    return null;
  }
}

export function detectLang() {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (SUPPORTED.includes(stored)) return stored;
    const nav = (navigator.language || navigator.userLanguage || "en").toLowerCase();
    if (nav.startsWith("es")) return "es";
    if (nav.startsWith("zh")) return "zh";
    if (nav.startsWith("vi")) return "vi";
    return "en";
  } catch {
    return "en";
  }
}

// Browser language before any manual override — used for the "view in your language" banner.
export function browserLang() {
  try {
    const nav = (navigator.language || navigator.userLanguage || "en").toLowerCase();
    if (nav.startsWith("es")) return "es";
    if (nav.startsWith("zh")) return "zh";
    if (nav.startsWith("vi")) return "vi";
    return "en";
  } catch {
    return "en";
  }
}

export function getLang() {
  return detectLang();
}

const STRINGS = {
  en: {
    nav_wellness: "Wellness", nav_library: "Health Library", nav_dashboard: "Dashboard", nav_disclaimer: "Disclaimer", nav_tools: "Free Tools",
    email_it: "Email it to me", email_answer_title: "Email this answer", email_desc: "Get a copy of this in your inbox.", email_ph: "you@email.com", email_send: "Send", email_sending: "Sending…", email_consent: "Email me occasional health tips (optional)", email_sent: "Sent! Check your inbox.", email_fail: "Couldn't send — please check the address and try again.",
    large_text: "Large Text", normal_text: "Normal Text",
    hero_badge: "Grounded in trusted U.S. government sources",
    hero_title_before: "Simple answers to everyday", hero_title_highlight: "medical questions",
    hero_subtitle: "Type or speak your question and get a clear, easy-to-understand answer — written in plain language, with the sources shown.",
    privacy_note: "For your privacy, do not enter your name, address, Social Security number, or other personal information.",
    try_these: "Try one of these", popular_questions: "Popular questions", everyday_healthy: "Everyday healthy living",
    see_all: "See all", topic_of_week: "Topic of the week", explore_topic: "Explore this topic",
    your_recent: "Your recent questions", clear: "Clear", browse_topic: "Browse by topic",
    qb_placeholder: "Ask a medical question...", qb_listening_placeholder: "Listening… speak your question",
    qb_listening_hint: "Listening… you can edit the text before you submit.",
    qb_ask: "Ask Question", qb_speak: "Speak Question", qb_stop: "Stop",
    qb_no_voice: "Voice input isn't available in this browser. Try Chrome, Edge, or Safari to speak your question.",
    disclaimer_text: "This service provides general medical information and education. It does not diagnose medical conditions or replace care from a physician or other qualified healthcare professional. If you may be experiencing a medical emergency, call 911.",
    emergency_heading: "This could be a medical emergency", call_911: "Call 911 Now",
    back_home: "Ask a new question", your_question: "Your Question",
    answer_loading: "Looking this up in trusted medical sources…", followup_heading: "One quick question first",
    followup_answer_placeholder: "Type your answer…", followup_continue: "Continue",
    listen_answer: "Listen to Answer", stop: "Stop", speed: "Speed", voice: "Voice", default_voice: "Default voice",
    sec_simple: "Simple Answer", sec_what: "What It Means", sec_contact: "When to Contact a Doctor", sec_urgent: "Get Urgent Help If…",
    sources: "Sources", sources_intro: "This information is based on these free U.S. government medical sources:",
    print_save: "Print / Save as PDF", simplify: "Explain Even Simpler", simplifying: "Simplifying…",
    simpler_heading: "In Even Simpler Words", helpful_q: "Was this helpful?", yes: "Yes", no: "No",
    feedback_thanks: "Thank you for your feedback!", writing_answer: "Writing your answer…",
    ask_followup: "Ask a Follow-Up Question", ask_doctor: "Ask a Doctor",
    lib_title: "Health Library",
    lib_subtitle: "Plain-language explanations of common health topics, backed by trusted U.S. government medical sources.",
    lib_search_placeholder: "Search topics… (e.g. diabetes, sciatica, cholesterol)",
    lib_no_results: "No topics matched your search. Try a different word, or ask your own question on the home page.",
    sec_conditions: "Conditions", sec_medications: "Medications", sec_tests: "Tests & Procedures", sec_wellness: "Wellness",
    learn_loading: "Loading this topic…", learn_why: "Why it matters", learn_tips: "Quick tips", learn_own_q: "Have your own question?",
    well_badge: "Everyday Wellness", well_title: "Healthy-Living Explorer",
    well_subtitle: "Short, friendly guides to the everyday habits that keep you well. Read all five to earn your certificate.",
    well_progress: "topics explored", well_reset: "Reset progress", well_all_done: "All done — you've explored every topic!", well_loading: "Loading wellness topics…", read_more: "Read more",
    cert_title: "Certificate of Wellness", cert_awarded_you: "Awarded to you",
    cert_name_ph: "Type your name for the certificate", cert_download: "Download image", cert_verify: "Verify this certificate",
    banner_switch: "Switch", read_topic: "Read topic", mark_read: "Mark as read", marked_read: "Read",
    cat_medication: "Medications", cat_symptom: "Symptoms", cat_disease: "Medical Conditions",
    cat_lab_test: "Lab Tests", cat_procedure: "Procedures", cat_mens_health: "Men's Health",
    cat_womens_health: "Women's Health", cat_general_wellness: "General Wellness",
    catex_medication: "What are the side effects of metformin?",
    catex_symptom: "I have had a cough for five days.",
    catex_disease: "What is atrial fibrillation?",
    catex_lab_test: "My A1C is 8.1. What does that mean?",
    catex_procedure: "What should I expect with a colonoscopy?",
    catex_mens_health: "What does a high PSA mean?",
    catex_womens_health: "What is a mammogram?",
    catex_general_wellness: "How much water should I drink?",
    related_reading: "Related Reading",
    upsell_a: "Want extra peace of mind? A licensed physician can review this answer for $19 and reply to you personally.",
    upsell_b: "You don't have to figure this out alone — for $19, a real doctor will personally check this answer and reply just for you.",
    upsell_cta: "Get it reviewed — $19",
    share_title: "Save & Share",
    share_desc: "Create a private link to this answer to save it or send it to a family member.",
    share_create: "Create Share Link", share_creating: "Creating link…",
    share_copy: "Copy Link", share_copied: "Copied!",
    doctor_desc: "Want a licensed physician to review your question? Choose an option below and a physician will follow up. This is separate from the free information above.",
    doctor_get_reviewed: "Get a Physician-Reviewed Answer",
    lab_nudge: "Asking about lab results? Choose Lab / data review ($39) and upload your labs so a physician can review your actual numbers.",
    tier_reviewed_title: "$19 · Reviewed answer", tier_reviewed_desc: "A physician reviews your question and replies.",
    tier_lab_title: "$39 · Lab / data review", tier_lab_desc: "Upload labs or medical records for a physician to review.",
    doctor_contact_ph: "Email so the physician can reach you (optional)",
    lab_attach_label: "Attach your labs or records (images or PDF, up to 3)", lab_add_file: "Add file",
    pay_10: "Pay $19 & Request Review", pay_20: "Pay $39 & Request Review", pay_redirecting: "Redirecting…",
    doctor_register_free: "Just register interest (free)",
    stripe_note: "Payments are securely processed by Stripe. Test mode — use card 4242 4242 4242 4242.",
    doctor_sent_msg: "Thanks! We've noted your interest in a physician review.",
    followup_ask_heading: "Ask a follow-up question", followup_ask_ph: "Ask another question about this…", followup_send: "Send Follow-Up",
    answered_in: "Answered in", browse_topic_click: "Tap a topic to ask about it",
    preview_voice: "Preview", voice_sample: "Hello, this is how this voice sounds.", choose_voice: "Choose a voice",
    read_in: "Read this in",
  },
  es: {
    nav_wellness: "Bienestar",
    email_it: "Envíamelo por correo", email_answer_title: "Enviar esta respuesta por correo", email_desc: "Recibe una copia en tu bandeja de entrada.", email_ph: "tu@correo.com", email_send: "Enviar", email_sending: "Enviando…", email_consent: "Envíenme consejos de salud ocasionales (opcional)", email_sent: "¡Enviado! Revisa tu correo.", email_fail: "No se pudo enviar — revisa la dirección e inténtalo de nuevo.", nav_library: "Biblioteca de Salud", nav_dashboard: "Panel", nav_disclaimer: "Aviso legal", nav_tools: "Herramientas",
    large_text: "Texto grande", normal_text: "Texto normal",
    hero_badge: "Basado en fuentes confiables del gobierno de EE. UU.",
    hero_title_before: "Respuestas sencillas a", hero_title_highlight: "preguntas médicas",
    hero_subtitle: "Escriba o diga su pregunta y obtenga una respuesta clara y fácil de entender, en lenguaje sencillo y con las fuentes mostradas.",
    privacy_note: "Para su privacidad, no ingrese su nombre, dirección, número de Seguro Social ni otra información personal.",
    try_these: "Pruebe una de estas", popular_questions: "Preguntas populares", everyday_healthy: "Vida saludable diaria",
    see_all: "Ver todo", topic_of_week: "Tema de la semana", explore_topic: "Explorar este tema",
    your_recent: "Sus preguntas recientes", clear: "Borrar", browse_topic: "Explorar por tema",
    qb_placeholder: "Haga una pregunta médica...", qb_listening_placeholder: "Escuchando… diga su pregunta",
    qb_listening_hint: "Escuchando… puede editar el texto antes de enviarlo.",
    qb_ask: "Hacer pregunta", qb_speak: "Hablar pregunta", qb_stop: "Detener",
    qb_no_voice: "La entrada por voz no está disponible en este navegador. Pruebe Chrome, Edge o Safari para hablar su pregunta.",
    disclaimer_text: "Este servicio ofrece información y educación médica general. No diagnostica afecciones médicas ni reemplaza la atención de un médico u otro profesional de la salud calificado. Si cree que puede estar sufriendo una emergencia médica, llame al 911.",
    emergency_heading: "Esto podría ser una emergencia médica", call_911: "Llame al 911 ahora",
    back_home: "Hacer una nueva pregunta", your_question: "Su pregunta",
    answer_loading: "Buscando esto en fuentes médicas de confianza…", followup_heading: "Primero una pregunta rápida",
    followup_answer_placeholder: "Escriba su respuesta…", followup_continue: "Continuar",
    listen_answer: "Escuchar respuesta", stop: "Detener", speed: "Velocidad", voice: "Voz", default_voice: "Voz predeterminada",
    sec_simple: "Respuesta sencilla", sec_what: "Qué significa", sec_contact: "Cuándo contactar a un médico", sec_urgent: "Busque ayuda urgente si…",
    sources: "Fuentes", sources_intro: "Esta información se basa en estas fuentes médicas gratuitas del gobierno de EE. UU.:",
    print_save: "Imprimir / Guardar como PDF", simplify: "Explicar aún más simple", simplifying: "Simplificando…",
    simpler_heading: "En palabras aún más simples", helpful_q: "¿Fue útil esto?", yes: "Sí", no: "No",
    feedback_thanks: "¡Gracias por sus comentarios!", writing_answer: "Escribiendo su respuesta…",
    ask_followup: "Hacer una pregunta de seguimiento", ask_doctor: "Consultar a un médico",
    lib_title: "Biblioteca de Salud",
    lib_subtitle: "Explicaciones en lenguaje sencillo de temas de salud comunes, respaldadas por fuentes médicas confiables del gobierno de EE. UU.",
    lib_search_placeholder: "Buscar temas… (p. ej. diabetes, ciática, colesterol)",
    lib_no_results: "Ningún tema coincidió con su búsqueda. Pruebe otra palabra o haga su propia pregunta en la página de inicio.",
    sec_conditions: "Afecciones", sec_medications: "Medicamentos", sec_tests: "Pruebas y procedimientos", sec_wellness: "Bienestar",
    learn_loading: "Cargando este tema…", learn_why: "Por qué importa", learn_tips: "Consejos rápidos", learn_own_q: "¿Tiene su propia pregunta?",
    well_badge: "Bienestar diario", well_title: "Explorador de Vida Saludable",
    well_subtitle: "Guías breves y amables sobre los hábitos diarios que lo mantienen sano. Lea las cinco para obtener su certificado.",
    well_progress: "temas explorados", well_reset: "Reiniciar progreso", well_all_done: "¡Listo! Ha explorado todos los temas.", well_loading: "Cargando temas de bienestar…", read_more: "Leer más",
    cert_title: "Certificado de Bienestar", cert_awarded_you: "Otorgado a usted",
    cert_name_ph: "Escriba su nombre para el certificado", cert_download: "Descargar imagen", cert_verify: "Verificar este certificado",
    banner_switch: "Cambiar", read_topic: "Leer tema", mark_read: "Marcar como leído", marked_read: "Leído",
    cat_medication: "Medicamentos", cat_symptom: "Síntomas", cat_disease: "Afecciones médicas",
    cat_lab_test: "Análisis de laboratorio", cat_procedure: "Procedimientos", cat_mens_health: "Salud masculina",
    cat_womens_health: "Salud femenina", cat_general_wellness: "Bienestar general",
    catex_medication: "¿Cuáles son los efectos secundarios de la metformina?",
    catex_symptom: "He tenido tos durante cinco días.",
    catex_disease: "¿Qué es la fibrilación auricular?",
    catex_lab_test: "Mi A1C es 8.1. ¿Qué significa?",
    catex_procedure: "¿Qué puedo esperar de una colonoscopia?",
    catex_mens_health: "¿Qué significa un PSA alto?",
    catex_womens_health: "¿Qué es una mamografía?",
    catex_general_wellness: "¿Cuánta agua debo beber?",
    related_reading: "Lecturas relacionadas",
    upsell_a: "¿Quiere mayor tranquilidad? Un médico con licencia puede revisar esta respuesta por $19 y responderle personalmente.",
    upsell_b: "No tiene que resolver esto solo: por $19, un médico real revisará esta respuesta y le responderá personalmente.",
    upsell_cta: "Que la revise un médico — $19",
    share_title: "Guardar y compartir",
    share_desc: "Cree un enlace privado a esta respuesta para guardarla o enviarla a un familiar.",
    share_create: "Crear enlace para compartir", share_creating: "Creando enlace…",
    share_copy: "Copiar enlace", share_copied: "¡Copiado!",
    doctor_desc: "¿Quiere que un médico con licencia revise su pregunta? Elija una opción a continuación y un médico le dará seguimiento. Esto es independiente de la información gratuita anterior.",
    doctor_get_reviewed: "Obtener una respuesta revisada por un médico",
    lab_nudge: "¿Pregunta sobre resultados de laboratorio? Elija Revisión de laboratorio / datos ($39) y suba sus análisis para que un médico revise sus valores reales.",
    tier_reviewed_title: "$19 · Respuesta revisada", tier_reviewed_desc: "Un médico revisa su pregunta y responde.",
    tier_lab_title: "$39 · Revisión de laboratorio / datos", tier_lab_desc: "Suba análisis o registros médicos para que un médico los revise.",
    doctor_contact_ph: "Correo para que el médico pueda contactarle (opcional)",
    lab_attach_label: "Adjunte sus análisis o registros (imágenes o PDF, hasta 3)", lab_add_file: "Agregar archivo",
    pay_10: "Pagar $19 y solicitar revisión", pay_20: "Pagar $39 y solicitar revisión", pay_redirecting: "Redirigiendo…",
    doctor_register_free: "Solo registrar interés (gratis)",
    stripe_note: "Los pagos se procesan de forma segura con Stripe. Modo de prueba — use la tarjeta 4242 4242 4242 4242.",
    doctor_sent_msg: "¡Gracias! Hemos registrado su interés en una revisión médica.",
    followup_ask_heading: "Haga una pregunta de seguimiento", followup_ask_ph: "Haga otra pregunta sobre esto…", followup_send: "Enviar seguimiento",
    answered_in: "Respondida en", browse_topic_click: "Toque un tema para preguntar",
    preview_voice: "Escuchar", voice_sample: "Hola, así suena esta voz.", choose_voice: "Elija una voz",
    read_in: "Leer esto en",
  },
  zh: {
    nav_wellness: "健康生活",
    email_it: "把结果发到我邮箱", email_answer_title: "把这条回答发到邮箱", email_desc: "在你的收件箱中获取一份副本。", email_ph: "you@email.com", email_send: "发送", email_sending: "发送中…", email_consent: "偶尔给我发送健康小贴士（可选）", email_sent: "已发送！请查收邮箱。", email_fail: "发送失败——请检查地址后重试。", nav_library: "健康知识库", nav_dashboard: "管理面板", nav_disclaimer: "免责声明", nav_tools: "免费工具",
    large_text: "大字体", normal_text: "普通字体",
    hero_badge: "基于可信的美国政府来源",
    hero_title_before: "用简单的方式解答", hero_title_highlight: "日常医疗问题",
    hero_subtitle: "输入或说出您的问题，获得清晰易懂的回答——以通俗语言给出，并附上信息来源。",
    privacy_note: "为保护您的隐私，请勿输入姓名、地址、社会安全号或其他个人信息。",
    try_these: "试试这些", popular_questions: "热门问题", everyday_healthy: "日常健康生活",
    see_all: "查看全部", topic_of_week: "本周主题", explore_topic: "了解此主题",
    your_recent: "您最近的问题", clear: "清除", browse_topic: "按主题浏览",
    qb_placeholder: "输入一个医疗问题...", qb_listening_placeholder: "正在聆听…请说出您的问题",
    qb_listening_hint: "正在聆听…提交前您可以编辑文字。",
    qb_ask: "提问", qb_speak: "语音提问", qb_stop: "停止",
    qb_no_voice: "此浏览器不支持语音输入。请尝试 Chrome、Edge 或 Safari 进行语音提问。",
    disclaimer_text: "本服务提供一般医疗信息和教育。它不诊断疾病，也不能替代医生或其他合格医疗专业人员的诊治。如果您可能正在经历医疗紧急情况，请拨打 911。",
    emergency_heading: "这可能是医疗紧急情况", call_911: "立即拨打 911",
    back_home: "提出新问题", your_question: "您的问题",
    answer_loading: "正在可信医疗来源中查找…", followup_heading: "先问一个小问题",
    followup_answer_placeholder: "输入您的回答…", followup_continue: "继续",
    listen_answer: "朗读回答", stop: "停止", speed: "语速", voice: "语音", default_voice: "默认语音",
    sec_simple: "简单解答", sec_what: "这意味着什么", sec_contact: "何时联系医生", sec_urgent: "出现以下情况请立即就医…",
    sources: "来源", sources_intro: "此信息基于以下免费的美国政府医疗来源：",
    print_save: "打印 / 保存为 PDF", simplify: "解释得更简单", simplifying: "正在简化…",
    simpler_heading: "用更简单的话说", helpful_q: "这有帮助吗？", yes: "有", no: "没有",
    feedback_thanks: "感谢您的反馈！", writing_answer: "正在生成您的回答…",
    ask_followup: "提出后续问题", ask_doctor: "咨询医生",
    lib_title: "健康知识库",
    lib_subtitle: "用通俗语言解释常见健康主题，依据可信的美国政府医疗来源。",
    lib_search_placeholder: "搜索主题…（例如：糖尿病、坐骨神经痛、胆固醇）",
    lib_no_results: "没有匹配的主题。请换个词，或在首页提出您自己的问题。",
    sec_conditions: "疾病", sec_medications: "药物", sec_tests: "检查与操作", sec_wellness: "健康生活",
    learn_loading: "正在加载此主题…", learn_why: "为什么重要", learn_tips: "小贴士", learn_own_q: "有自己的问题吗？",
    well_badge: "日常健康", well_title: "健康生活探索者",
    well_subtitle: "关于让您保持健康的日常习惯的简短友好指南。读完全部五个即可获得证书。",
    well_progress: "个主题已浏览", well_reset: "重置进度", well_all_done: "全部完成——您已浏览所有主题！", well_loading: "正在加载健康主题…", read_more: "阅读更多",
    cert_title: "健康证书", cert_awarded_you: "颁发给您",
    cert_name_ph: "输入您的名字以显示在证书上", cert_download: "下载图片", cert_verify: "验证此证书",
    banner_switch: "切换", read_topic: "阅读主题", mark_read: "标记为已读", marked_read: "已读",
    cat_medication: "药物", cat_symptom: "症状", cat_disease: "疾病",
    cat_lab_test: "化验检查", cat_procedure: "医疗操作", cat_mens_health: "男性健康",
    cat_womens_health: "女性健康", cat_general_wellness: "综合健康",
    catex_medication: "二甲双胍有哪些副作用？",
    catex_symptom: "我咳嗽已经五天了。",
    catex_disease: "什么是心房颤动？",
    catex_lab_test: "我的糖化血红蛋白（A1C）是 8.1，这意味着什么？",
    catex_procedure: "做结肠镜检查会经历什么？",
    catex_mens_health: "PSA 偏高意味着什么？",
    catex_womens_health: "什么是乳房X光检查？",
    catex_general_wellness: "我每天应该喝多少水？",
    related_reading: "相关阅读",
    upsell_a: "想要更安心吗？持证医生可以用 $19 审阅这个回答并亲自回复您。",
    upsell_b: "您不必独自面对——只需 $19，一位真正的医生会亲自审阅这个回答并专门回复您。",
    upsell_cta: "请医生审阅 — $19",
    share_title: "保存与分享",
    share_desc: "为这个回答创建一个私密链接，方便保存或发送给家人。",
    share_create: "创建分享链接", share_creating: "正在创建链接…",
    share_copy: "复制链接", share_copied: "已复制！",
    doctor_desc: "想让持证医生审阅您的问题吗？请在下面选择一个选项，医生会跟进。这与上面的免费信息是分开的。",
    doctor_get_reviewed: "获取医生审阅的回答",
    lab_nudge: "在询问化验结果吗？请选择化验／资料审阅（$39）并上传您的化验单，让医生审阅您的实际数值。",
    tier_reviewed_title: "$19 · 审阅回答", tier_reviewed_desc: "医生审阅您的问题并回复。",
    tier_lab_title: "$39 · 化验／资料审阅", tier_lab_desc: "上传化验单或病历，供医生审阅。",
    doctor_contact_ph: "填写邮箱以便医生联系您（可选）",
    lab_attach_label: "上传您的化验单或病历（图片或 PDF，最多 3 个）", lab_add_file: "添加文件",
    pay_10: "支付 $19 并申请审阅", pay_20: "支付 $39 并申请审阅", pay_redirecting: "正在跳转…",
    doctor_register_free: "仅登记意向（免费）",
    stripe_note: "付款由 Stripe 安全处理。测试模式——请使用卡号 4242 4242 4242 4242。",
    doctor_sent_msg: "谢谢！我们已记录您希望获得医生审阅的意向。",
    followup_ask_heading: "提出后续问题", followup_ask_ph: "就此再问一个问题…", followup_send: "发送后续问题",
    answered_in: "回答语言：", browse_topic_click: "点击一个主题即可提问",
    preview_voice: "试听", voice_sample: "您好，这是这个声音的效果。", choose_voice: "选择一个语音",
    read_in: "使用其他语言阅读：",
  },
  vi: {
    nav_wellness: "Sức khỏe",
    email_it: "Gửi vào email của tôi", email_answer_title: "Gửi câu trả lời này qua email", email_desc: "Nhận một bản sao trong hộp thư của bạn.", email_ph: "ban@email.com", email_send: "Gửi", email_sending: "Đang gửi…", email_consent: "Thỉnh thoảng gửi cho tôi mẹo sức khỏe (tùy chọn)", email_sent: "Đã gửi! Hãy kiểm tra hộp thư.", email_fail: "Không gửi được — vui lòng kiểm tra địa chỉ và thử lại.", nav_library: "Thư viện Sức khỏe", nav_dashboard: "Bảng điều khiển", nav_disclaimer: "Tuyên bố miễn trừ", nav_tools: "Công cụ miễn phí",
    large_text: "Chữ lớn", normal_text: "Chữ thường",
    hero_badge: "Dựa trên nguồn đáng tin cậy của chính phủ Hoa Kỳ",
    hero_title_before: "Câu trả lời đơn giản cho", hero_title_highlight: "câu hỏi y tế thường ngày",
    hero_subtitle: "Nhập hoặc nói câu hỏi của bạn và nhận câu trả lời rõ ràng, dễ hiểu — bằng ngôn ngữ đơn giản, kèm nguồn tham khảo.",
    privacy_note: "Để bảo vệ quyền riêng tư, đừng nhập tên, địa chỉ, số An sinh Xã hội hoặc thông tin cá nhân khác.",
    try_these: "Thử một trong số này", popular_questions: "Câu hỏi phổ biến", everyday_healthy: "Sống khỏe mỗi ngày",
    see_all: "Xem tất cả", topic_of_week: "Chủ đề trong tuần", explore_topic: "Khám phá chủ đề này",
    your_recent: "Câu hỏi gần đây của bạn", clear: "Xóa", browse_topic: "Duyệt theo chủ đề",
    qb_placeholder: "Đặt một câu hỏi y tế...", qb_listening_placeholder: "Đang nghe… hãy nói câu hỏi của bạn",
    qb_listening_hint: "Đang nghe… bạn có thể chỉnh sửa văn bản trước khi gửi.",
    qb_ask: "Đặt câu hỏi", qb_speak: "Hỏi bằng giọng nói", qb_stop: "Dừng",
    qb_no_voice: "Trình duyệt này không hỗ trợ nhập bằng giọng nói. Hãy thử Chrome, Edge hoặc Safari.",
    disclaimer_text: "Dịch vụ này cung cấp thông tin và giáo dục y tế tổng quát. Nó không chẩn đoán bệnh và không thay thế việc chăm sóc của bác sĩ hoặc chuyên gia y tế có trình độ. Nếu bạn có thể đang gặp trường hợp cấp cứu y tế, hãy gọi 911.",
    emergency_heading: "Đây có thể là trường hợp cấp cứu y tế", call_911: "Gọi 911 ngay",
    back_home: "Đặt câu hỏi mới", your_question: "Câu hỏi của bạn",
    answer_loading: "Đang tra cứu trong các nguồn y tế đáng tin cậy…", followup_heading: "Một câu hỏi nhanh trước đã",
    followup_answer_placeholder: "Nhập câu trả lời của bạn…", followup_continue: "Tiếp tục",
    listen_answer: "Nghe câu trả lời", stop: "Dừng", speed: "Tốc độ", voice: "Giọng", default_voice: "Giọng mặc định",
    sec_simple: "Câu trả lời đơn giản", sec_what: "Điều đó nghĩa là gì", sec_contact: "Khi nào cần liên hệ bác sĩ", sec_urgent: "Hãy tìm trợ giúp khẩn cấp nếu…",
    sources: "Nguồn", sources_intro: "Thông tin này dựa trên các nguồn y tế miễn phí của chính phủ Hoa Kỳ:",
    print_save: "In / Lưu PDF", simplify: "Giải thích đơn giản hơn nữa", simplifying: "Đang đơn giản hóa…",
    simpler_heading: "Bằng lời còn đơn giản hơn", helpful_q: "Điều này có hữu ích không?", yes: "Có", no: "Không",
    feedback_thanks: "Cảm ơn phản hồi của bạn!", writing_answer: "Đang viết câu trả lời của bạn…",
    ask_followup: "Đặt câu hỏi tiếp theo", ask_doctor: "Hỏi bác sĩ",
    lib_title: "Thư viện Sức khỏe",
    lib_subtitle: "Giải thích bằng ngôn ngữ đơn giản về các chủ đề sức khỏe thường gặp, dựa trên nguồn y tế đáng tin cậy của chính phủ Hoa Kỳ.",
    lib_search_placeholder: "Tìm chủ đề… (vd: tiểu đường, đau thần kinh tọa, cholesterol)",
    lib_no_results: "Không có chủ đề nào khớp. Hãy thử từ khác, hoặc đặt câu hỏi của bạn ở trang chủ.",
    sec_conditions: "Bệnh lý", sec_medications: "Thuốc", sec_tests: "Xét nghiệm & Thủ thuật", sec_wellness: "Sức khỏe",
    learn_loading: "Đang tải chủ đề này…", learn_why: "Vì sao quan trọng", learn_tips: "Mẹo nhanh", learn_own_q: "Bạn có câu hỏi riêng?",
    well_badge: "Sức khỏe hằng ngày", well_title: "Nhà Khám phá Sống khỏe",
    well_subtitle: "Hướng dẫn ngắn gọn, thân thiện về những thói quen hằng ngày giúp bạn khỏe mạnh. Đọc cả năm để nhận chứng nhận.",
    well_progress: "chủ đề đã khám phá", well_reset: "Đặt lại tiến trình", well_all_done: "Hoàn tất — bạn đã khám phá tất cả chủ đề!", well_loading: "Đang tải chủ đề sức khỏe…", read_more: "Đọc thêm",
    cert_title: "Chứng nhận Sức khỏe", cert_awarded_you: "Trao cho bạn",
    cert_name_ph: "Nhập tên của bạn cho chứng nhận", cert_download: "Tải hình ảnh", cert_verify: "Xác minh chứng nhận này",
    banner_switch: "Chuyển", read_topic: "Đọc chủ đề", mark_read: "Đánh dấu đã đọc", marked_read: "Đã đọc",
    cat_medication: "Thuốc", cat_symptom: "Triệu chứng", cat_disease: "Bệnh lý",
    cat_lab_test: "Xét nghiệm", cat_procedure: "Thủ thuật", cat_mens_health: "Sức khỏe nam giới",
    cat_womens_health: "Sức khỏe nữ giới", cat_general_wellness: "Sức khỏe tổng quát",
    catex_medication: "Metformin có tác dụng phụ gì?",
    catex_symptom: "Tôi bị ho đã năm ngày.",
    catex_disease: "Rung nhĩ là gì?",
    catex_lab_test: "Chỉ số A1C của tôi là 8.1. Điều đó nghĩa là gì?",
    catex_procedure: "Tôi nên chuẩn bị gì cho nội soi đại tràng?",
    catex_mens_health: "Chỉ số PSA cao nghĩa là gì?",
    catex_womens_health: "Chụp nhũ ảnh là gì?",
    catex_general_wellness: "Tôi nên uống bao nhiêu nước?",
    related_reading: "Bài đọc liên quan",
    upsell_a: "Muốn yên tâm hơn? Một bác sĩ có giấy phép có thể xem lại câu trả lời này với giá $19 và trả lời riêng cho bạn.",
    upsell_b: "Bạn không phải tự mình giải quyết — chỉ với $19, một bác sĩ thực thụ sẽ đích thân kiểm tra câu trả lời này và trả lời riêng cho bạn.",
    upsell_cta: "Nhờ bác sĩ xem lại — $19",
    share_title: "Lưu & Chia sẻ",
    share_desc: "Tạo một liên kết riêng tư đến câu trả lời này để lưu lại hoặc gửi cho người thân.",
    share_create: "Tạo liên kết chia sẻ", share_creating: "Đang tạo liên kết…",
    share_copy: "Sao chép liên kết", share_copied: "Đã sao chép!",
    doctor_desc: "Muốn một bác sĩ có giấy phép xem lại câu hỏi của bạn? Hãy chọn một tùy chọn bên dưới và bác sĩ sẽ liên hệ. Việc này tách biệt với thông tin miễn phí ở trên.",
    doctor_get_reviewed: "Nhận câu trả lời được bác sĩ xem lại",
    lab_nudge: "Bạn đang hỏi về kết quả xét nghiệm? Hãy chọn Xem xét nghiệm / dữ liệu ($39) và tải lên phiếu xét nghiệm để bác sĩ xem các chỉ số thực tế của bạn.",
    tier_reviewed_title: "$19 · Câu trả lời được xem lại", tier_reviewed_desc: "Bác sĩ xem lại câu hỏi của bạn và trả lời.",
    tier_lab_title: "$39 · Xem xét nghiệm / dữ liệu", tier_lab_desc: "Tải lên phiếu xét nghiệm hoặc hồ sơ y tế để bác sĩ xem.",
    doctor_contact_ph: "Email để bác sĩ có thể liên hệ với bạn (không bắt buộc)",
    lab_attach_label: "Đính kèm phiếu xét nghiệm hoặc hồ sơ của bạn (hình ảnh hoặc PDF, tối đa 3)", lab_add_file: "Thêm tệp",
    pay_10: "Thanh toán $19 & Yêu cầu xem lại", pay_20: "Thanh toán $39 & Yêu cầu xem lại", pay_redirecting: "Đang chuyển hướng…",
    doctor_register_free: "Chỉ đăng ký quan tâm (miễn phí)",
    stripe_note: "Thanh toán được xử lý an toàn bởi Stripe. Chế độ thử nghiệm — dùng thẻ 4242 4242 4242 4242.",
    doctor_sent_msg: "Cảm ơn! Chúng tôi đã ghi nhận bạn quan tâm đến việc bác sĩ xem lại.",
    followup_ask_heading: "Đặt câu hỏi tiếp theo", followup_ask_ph: "Hỏi thêm một câu về điều này…", followup_send: "Gửi câu hỏi tiếp theo",
    answered_in: "Trả lời bằng", browse_topic_click: "Chạm vào một chủ đề để hỏi",
    preview_voice: "Nghe thử", voice_sample: "Xin chào, đây là giọng đọc này.", choose_voice: "Chọn một giọng đọc",
    read_in: "Đọc bằng",
  },
};

// Banner shown in the visitor's OWN language inviting them to switch.
export const BANNER_INVITE = {
  es: "Este sitio está disponible en Español.",
  zh: "本网站提供中文版本。",
  vi: "Trang web này có sẵn bằng Tiếng Việt.",
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => urlLang() || detectLang());

  useEffect(() => {
    const u = urlLang();
    if (u && u !== lang) {
      setLangState(u);
      document.documentElement.lang = u;
      try {
        localStorage.setItem(LANG_KEY, u);
      } catch {
        /* ignore */
      }
    }
    // Run once on mount to honor a ?hl= landing URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLang = useCallback((l) => {
    const next = SUPPORTED.includes(l) ? l : "en";
    try {
      localStorage.setItem(LANG_KEY, next);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = next;
    setLangState(next);
  }, []);

  const t = useCallback(
    (key) => (STRINGS[lang] && STRINGS[lang][key]) || STRINGS.en[key] || key,
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}

export function useT() {
  const ctx = useContext(LanguageContext);
  return ctx ? ctx.t : (k) => STRINGS.en[k] || k;
}

// Translate a specific key in a specific language (independent of the UI language).
export function tr(lang, key) {
  return (STRINGS[lang] && STRINGS[lang][key]) || STRINGS.en[key] || key;
}
